import React, { useState, useEffect, useRef } from 'react'
import { Button } from '@mui/material'
import Dialog from '@/product/components/organisms/Dialog'

// Component that loads the scanner library dynamically
const BarCodeScan = ({
    scanToggle,
    changeScanDialogStatus,
    setDecodedBarcode,
}) => {
    const [result, setResult] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [isScanning, setIsScanning] = useState(false)
    const [cameraList, setCameraList] = useState([])
    const [selectedCamera, setSelectedCamera] = useState(null)
    const [libraryLoaded, setLibraryLoaded] = useState(false)
    const [loadingLibrary, setLoadingLibrary] = useState(false)
    const [torchEnabled, setTorchEnabled] = useState(false)
    const [lastDetectedText, setLastDetectedText] = useState('')
    const [detectionAttempts, setDetectionAttempts] = useState(0)

    const videoRef = useRef(null)
    const canvasRef = useRef(null)
    const codeReader = useRef(null)
    const streamRef = useRef(null)
    const scannerTimeoutRef = useRef(null)
    const animationFrameRef = useRef(null)

    // Load the zxing-js library when component mounts
    useEffect(() => {
        if (!window.ZXing && !loadingLibrary) {
            loadLibrary()
        } else if (window.ZXing) {
            setLibraryLoaded(true)
        }
    }, [])

    // Initialize scanner and start scanning when dialog opens
    useEffect(() => {
        if (scanToggle && libraryLoaded) {
            console.log('Dialog opened, initializing scanner')
            initScanner()
        }

        if (!scanToggle) {
            // Clean up when dialog closes
            resetScanner()
        }

        return () => {
            resetScanner()
        }
    }, [scanToggle, libraryLoaded])

    // Load the zxing-js library dynamically
    const loadLibrary = () => {
        setLoadingLibrary(true)
        setErrorMessage('Loading scanner library...')

        // Try the original 0.15.0 version which has better support for some barcodes
        const script = document.createElement('script')
        script.src = 'https://unpkg.com/@zxing/library@0.15.0/umd/index.min.js'
        script.async = true

        script.onload = () => {
            console.log('ZXing library loaded successfully')
            setLibraryLoaded(true)
            setLoadingLibrary(false)
            setErrorMessage('')

            if (scanToggle) {
                initScanner()
            }
        }

        script.onerror = () => {
            console.error('Failed to load ZXing library')
            setErrorMessage(
                'Failed to load scanner library. Please check your internet connection.'
            )
            setLoadingLibrary(false)
        }

        document.body.appendChild(script)
    }

    // Initialize the scanner and start scanning automatically
    const initScanner = async () => {
        // Reset states
        setResult('')
        setErrorMessage('')
        setIsScanning(false)
        setTorchEnabled(false)
        setLastDetectedText('')
        setDetectionAttempts(0)

        if (!window.ZXing) {
            setErrorMessage(
                'Scanner library not loaded properly. Trying again...'
            )
            loadLibrary()
            return
        }

        console.log('Initializing the scanner...')

        try {
            // Configure hints for code reader - prioritizing linear barcodes
            const hints = new Map()
            const formats = [
                window.ZXing.BarcodeFormat.CODE_128, // High priority for CODE-128 (seen in image)
                window.ZXing.BarcodeFormat.UPC_A,
                window.ZXing.BarcodeFormat.UPC_E,
                window.ZXing.BarcodeFormat.EAN_13,
                window.ZXing.BarcodeFormat.EAN_8,
                window.ZXing.BarcodeFormat.CODE_39,
                window.ZXing.BarcodeFormat.CODABAR,
                window.ZXing.BarcodeFormat.ITF,
                window.ZXing.BarcodeFormat.RSS_14,
                window.ZXing.BarcodeFormat.QR_CODE,
                window.ZXing.BarcodeFormat.DATA_MATRIX,
            ]
            hints.set(window.ZXing.DecodeHintType.POSSIBLE_FORMATS, formats)
            hints.set(window.ZXing.DecodeHintType.TRY_HARDER, true)

            // Create a canvas for custom barcode processing
            if (!canvasRef.current) {
                canvasRef.current = document.createElement('canvas')
            }

            // Create a new code reader instance with hints
            if (!codeReader.current) {
                codeReader.current = new window.ZXing.BrowserMultiFormatReader(
                    hints,
                    500
                )
            } else {
                try {
                    codeReader.current.reset()
                    codeReader.current =
                        new window.ZXing.BrowserMultiFormatReader(hints, 500)
                } catch (e) {
                    console.log('Reset error, creating new instance')
                    codeReader.current =
                        new window.ZXing.BrowserMultiFormatReader(hints, 500)
                }
            }

            // List available cameras
            try {
                const devices = await codeReader.current.listVideoInputDevices()
                console.log('Available cameras:', devices)

                if (devices && devices.length > 0) {
                    setCameraList(devices)

                    // Try to select back camera by default
                    const backCamera = devices.find(
                        (device) =>
                            device.label.toLowerCase().includes('back') ||
                            device.label.toLowerCase().includes('rear') ||
                            device.label.toLowerCase().includes('environment')
                    )

                    const cameraToUse = backCamera || devices[0]
                    console.log('Selected camera:', cameraToUse)
                    setSelectedCamera(cameraToUse)

                    // Start scanning automatically after camera selection
                    startScanning(cameraToUse.deviceId)
                } else {
                    setErrorMessage(
                        'No cameras found. Please ensure camera permissions are granted.'
                    )
                }
            } catch (err) {
                console.error('Error listing cameras:', err)
                setErrorMessage(`Error accessing cameras: ${err.message}`)
            }
        } catch (error) {
            console.error('Error initializing scanner:', error)
            setErrorMessage(
                `Initialization error: ${error.message || 'Unknown error'}`
            )
        }
    }

    // Start scanning with the selected camera
    const startScanning = (deviceId) => {
        if (!codeReader.current || !videoRef.current) {
            setErrorMessage('Scanner not initialized properly')
            return
        }

        setIsScanning(true)
        console.log(
            `Starting scan with device ID: ${deviceId}`,
            codeReader.current,
            videoRef.current
        )

        // Use higher quality video for better barcode recognition
        const constraints = {
            video: {
                deviceId: deviceId ? { exact: deviceId } : undefined,
                width: { ideal: 1920 }, // Higher resolution
                height: { ideal: 1080 }, // Higher resolution
                facingMode: 'environment',
                focusMode: 'continuous', // Enable continuous focus
                brightness: { ideal: 100 },
                contrast: { ideal: 100 },
                sharpness: { ideal: 100 },
            },
        }

        // Use both the library scan and our custom frame processing
        codeReader.current
            .decodeFromConstraints(
                constraints,
                videoRef.current,
                (result, err) => {
                    // Success case
                    if (result) {
                        handleBarcodeDetection(result.text)
                    }

                    // Error case - but ignore NotFoundException as it's expected
                    if (
                        err &&
                        !(err instanceof window.ZXing.NotFoundException)
                    ) {
                        console.error('Decode error:', err)
                        // Only show fatal errors to the user
                        if (
                            !(err instanceof window.ZXing.ChecksumException) &&
                            !(err instanceof window.ZXing.FormatException)
                        ) {
                            setErrorMessage(
                                `Scanner error: ${err.message || err}`
                            )
                        }
                    }
                }
            )
            .then((stream) => {
                // Store stream reference for torch control
                streamRef.current = stream

                // Check for torch capability
                if (stream) {
                    const videoTrack = stream.getVideoTracks()[0]
                    if (videoTrack) {
                        // Try to set higher quality settings for the camera
                        try {
                            const capabilities =
                                videoTrack.getCapabilities &&
                                videoTrack.getCapabilities()
                            console.log(
                                'Video track capabilities:',
                                capabilities
                            )

                            // Apply advanced camera settings if supported
                            if (videoTrack.applyConstraints) {
                                const advancedSettings = {}

                                // Add torch if available
                                if (capabilities && capabilities.torch) {
                                    advancedSettings.torch = false
                                    const torchButton =
                                        document.getElementById('torch-button')
                                    if (torchButton) {
                                        torchButton.style.display =
                                            'inline-block'
                                    }
                                }

                                // Add focus if available
                                if (
                                    capabilities &&
                                    capabilities.focusMode &&
                                    capabilities.focusMode.includes(
                                        'continuous'
                                    )
                                ) {
                                    advancedSettings.focusMode = 'continuous'
                                }

                                // Apply settings if we have any
                                if (Object.keys(advancedSettings).length > 0) {
                                    videoTrack
                                        .applyConstraints({
                                            advanced: [advancedSettings],
                                        })
                                        .catch((e) =>
                                            console.log(
                                                'Could not apply advanced constraints:',
                                                e
                                            )
                                        )
                                }
                            }
                        } catch (e) {
                            console.log(
                                'Error accessing camera capabilities:',
                                e
                            )
                        }

                        // Set up manual frame processing for better barcode detection
                        startManualFrameProcessing()
                    }
                }
            })
            .catch((err) => {
                console.error('Error starting scan:', err)
                setErrorMessage(
                    `Failed to start scanner: ${err.message || 'Unknown error'}`
                )
                setIsScanning(false)
            })
    }

    // Process video frames manually for better barcode detection
    const startManualFrameProcessing = () => {
        if (!videoRef.current || !canvasRef.current) return

        // Set up timeout for periodic frame captures every 1 second
        // This is in addition to the library's scanning
        scannerTimeoutRef.current = setInterval(() => {
            if (!videoRef.current || !videoRef.current.videoWidth) return

            try {
                // Set canvas dimensions to match video
                canvasRef.current.width = videoRef.current.videoWidth
                canvasRef.current.height = videoRef.current.videoHeight

                // Get 2D context and draw the current video frame
                const ctx = canvasRef.current.getContext('2d')
                ctx.drawImage(videoRef.current, 0, 0)

                // Try to detect barcode in the captured frame
                processFrame(ctx)
            } catch (e) {
                console.log('Error in manual frame processing:', e)
            }
        }, 1000)
    }

    // Process a single frame to try to detect barcodes
    const processFrame = (ctx) => {
        if (!codeReader.current || !isScanning) return

        try {
            // Create bitmap from canvas
            const luminanceSource =
                new window.ZXing.HTMLCanvasElementLuminanceSource(
                    canvasRef.current
                )

            // Try multiple binarizers for different lighting conditions
            const binarizers = [
                new window.ZXing.HybridBinarizer(luminanceSource),
                new window.ZXing.GlobalHistogramBinarizer(luminanceSource),
            ]

            // Try both binarizers
            for (const binarizer of binarizers) {
                try {
                    const bitmap = new window.ZXing.BinaryBitmap(binarizer)
                    const reader = new window.ZXing.MultiFormatReader()

                    // Configure reader
                    const hints = new Map()
                    hints.set(window.ZXing.DecodeHintType.TRY_HARDER, true)
                    reader.setHints(hints)

                    // Try to decode
                    const result = reader.decode(bitmap)
                    if (result && result.text) {
                        handleBarcodeDetection(result.text)
                        return
                    }
                } catch (error) {
                    // Ignore expected errors
                    if (
                        !(error instanceof window.ZXing.NotFoundException) &&
                        !(error instanceof window.ZXing.ChecksumException) &&
                        !(error instanceof window.ZXing.FormatException)
                    ) {
                        console.log('Error in barcode detection:', error)
                    }
                }
            }
        } catch (e) {
            console.log('Error in frame processing:', e)
        }
    }

    // Handle detected barcode text with verification
    const handleBarcodeDetection = (text) => {
        console.log('Potential barcode detected:', text)

        // Only filter out single special characters or very short invalid inputs
        // This allows 2-3 digit numbers while filtering out single characters like "+"
        if (
            text.length === 1 ||
            (text.length < 3 && /^[^0-9a-zA-Z]+$/.test(text))
        ) {
            console.log('Ignoring invalid result:', text)
            return
        }

        // Special handling for OH format barcode
        if (text.match(/^OH001\.05-\d+-\d+$/)) {
            console.log('Detected valid barcode in expected format:', text)
            // Accept immediately
            setResult(text)
            setDecodedBarcode(text)

            // Close the dialog
            if (scanToggle) {
                changeScanDialogStatus(false)
            }
            return
        }

        // Compare with last detected text to verify consistency
        if (text === lastDetectedText) {
            setDetectionAttempts((prev) => {
                // Accept faster for numeric codes
                const isNumeric = /^\d+$/.test(text)
                // If we've seen this result multiple times, it's likely correct
                if (prev >= 0 || isNumeric) {
                    console.log('Barcode confirmed:', text)
                    setResult(text)
                    setDecodedBarcode(text)

                    // Close the dialog
                    if (scanToggle) {
                        changeScanDialogStatus(false)
                    }
                    return 0
                }
                return prev + 1
            })
        } else {
            // New text detected, restart verification
            setLastDetectedText(text)
            setDetectionAttempts(1)

            // Accept numeric results immediately (including short 2-3 digit numbers)
            if (/^\d+$/.test(text)) {
                console.log('Numeric barcode detected:', text)
                setResult(text)
                setDecodedBarcode(text)

                // Close the dialog with a delay for visual feedback
                if (scanToggle) {
                    setTimeout(() => changeScanDialogStatus(false), 300)
                }
            }
            // Also accept long alphanumeric results on first detection
            else if (text.length > 5) {
                console.log('Potentially valid long barcode detected:', text)
                setResult(text)
                setDecodedBarcode(text)

                // Close the dialog with a delay for visual feedback
                if (scanToggle) {
                    setTimeout(() => changeScanDialogStatus(false), 500)
                }
            }
        }
    }

    // Toggle torch/flashlight
    const toggleTorch = async () => {
        if (!streamRef.current) {
            console.log('No stream available for torch control')
            return
        }

        try {
            const videoTracks = streamRef.current.getVideoTracks()
            if (videoTracks && videoTracks.length > 0) {
                const videoTrack = videoTracks[0]
                if (videoTrack) {
                    const newTorchState = !torchEnabled
                    console.log('Setting torch state to:', newTorchState)
                    await videoTrack.applyConstraints({
                        advanced: [{ torch: newTorchState }],
                    })
                    setTorchEnabled(newTorchState)
                }
            } else {
                console.log('No video tracks found in stream')
                setErrorMessage('No video track found')
            }
        } catch (error) {
            console.error('Error toggling torch:', error)
            setErrorMessage(
                "Couldn't toggle flashlight. Your device may not support this feature."
            )
        }
    }

    // Reset and cleanup scanner resources
    const resetScanner = () => {
        console.log('Resetting scanner...')

        // Clear any timeouts or animation frames
        if (scannerTimeoutRef.current) {
            clearInterval(scannerTimeoutRef.current)
            scannerTimeoutRef.current = null
        }

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
            animationFrameRef.current = null
        }

        // Stop any active scanning
        if (codeReader.current) {
            try {
                codeReader.current.reset()
            } catch (e) {
                console.log('Error resetting scanner:', e)
            }
        }

        // Release video stream if active
        if (streamRef.current) {
            const tracks = streamRef.current.getTracks()
            tracks.forEach((track) => track.stop())
            streamRef.current = null
        }

        setIsScanning(false)
        setTorchEnabled(false)
    }

    // Handle camera selection change
    const handleCameraChange = (event) => {
        const cameraId = event.target.value
        const camera = cameraList.find((cam) => cam.deviceId === cameraId)

        if (camera) {
            console.log('Changing camera to:', camera.label)
            resetScanner()
            setSelectedCamera(camera)

            // Start scanning with the newly selected camera
            startScanning(camera.deviceId)
        }
    }

    // Handle restart scanning
    const handleRestart = () => {
        resetScanner()
        if (selectedCamera) {
            startScanning(selectedCamera.deviceId)
        } else if (cameraList && cameraList.length > 0) {
            startScanning(cameraList[0].deviceId)
        }
    }

    // Handle close button
    const handleClose = () => {
        console.log('Closing scanner dialog')
        resetScanner()
        changeScanDialogStatus(false)
    }

    // Don't render anything if dialog is closed
    if (!scanToggle) {
        return null
    }

    return (
        <Dialog
            title="Scan Barcode"
            open={scanToggle}
            className="scanDialog"
            function={handleClose}
            handleClose={handleClose}
        >
            <div className="scannerBox">
                <div
                    style={{
                        width: '100%',
                        maxWidth: '500px',
                        margin: '0 auto',
                    }}
                >
                    {/* Controls */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '15px',
                        }}
                    >
                        <div>
                            <Button
                                variant="outlined"
                                onClick={handleRestart}
                                disabled={!libraryLoaded}
                            >
                                RESET
                            </Button>
                            <Button
                                id="torch-button"
                                variant="outlined"
                                style={{ marginLeft: '10px', display: 'none' }}
                                onClick={toggleTorch}
                                disabled={!isScanning}
                            >
                                {torchEnabled ? 'LIGHT OFF' : 'LIGHT ON'}
                            </Button>
                        </div>
                        <Button onClick={handleClose}>CLOSE</Button>
                    </div>

                    {/* Camera selector */}
                    {cameraList.length > 1 && (
                        <div style={{ margin: '15px 0' }}>
                            <label
                                htmlFor="cameraSelect"
                                style={{
                                    display: 'block',
                                    marginBottom: '5px',
                                }}
                            >
                                Change camera:
                            </label>
                            <select
                                id="cameraSelect"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                }}
                                onChange={handleCameraChange}
                                value={selectedCamera?.deviceId || ''}
                            >
                                {cameraList.map((camera) => (
                                    <option
                                        key={camera.deviceId}
                                        value={camera.deviceId}
                                    >
                                        {camera.label ||
                                            `Camera ${camera.deviceId}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Video container */}
                    <div
                        style={{
                            width: '100%',
                            height: '300px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }}
                    >
                        {loadingLibrary ? (
                            <div
                                style={{ textAlign: 'center', padding: '20px' }}
                            >
                                Loading scanner library...
                            </div>
                        ) : (
                            <>
                                <video
                                    ref={videoRef}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                    muted
                                    playsInline
                                />
                                {/* Overlay scanning area indicator - wider for linear barcodes */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        width: '280px', // Wider to accommodate linear barcodes
                                        height: '160px', // Not as tall, more rectangular
                                        border: '2px solid #ffffff',
                                        borderRadius: '10px',
                                        boxShadow:
                                            '0 0 0 3000px rgba(0, 0, 0, 0.3)',
                                        zIndex: 9,
                                    }}
                                />
                                {/* Helper instruction text */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        bottom: '20px',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        padding: '8px 16px',
                                        borderRadius: '20px',
                                        zIndex: 10,
                                    }}
                                >
                                    Position barcode inside the box
                                </div>
                            </>
                        )}
                    </div>

                    {/* Error message */}
                    {errorMessage && (
                        <div
                            style={{
                                color: loadingLibrary ? 'black' : 'red',
                                margin: '10px 0',
                                padding: '8px',
                                backgroundColor: loadingLibrary
                                    ? '#e8eaf6'
                                    : '#ffebee',
                                borderRadius: '4px',
                            }}
                        >
                            {errorMessage}
                        </div>
                    )}

                    {/* Result */}
                    {result && (
                        <div style={{ margin: '15px 0' }}>
                            <label
                                style={{
                                    fontWeight: 'bold',
                                    display: 'block',
                                    marginBottom: '5px',
                                }}
                            >
                                Result:
                            </label>
                            <pre
                                style={{
                                    backgroundColor: '#f5f5f5',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    overflowX: 'auto',
                                    wordBreak: 'break-all',
                                }}
                            >
                                <code>{result}</code>
                            </pre>
                        </div>
                    )}

                    {/* Troubleshooting tips */}
                    <div
                        style={{
                            margin: '15px 0',
                            padding: '10px',
                            backgroundColor: '#e8f5e9',
                            borderRadius: '4px',
                        }}
                    >
                        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                            Tips for linear barcode scanning:
                        </p>
                        <ul style={{ margin: '0', paddingLeft: '20px' }}>
                            <li>
                                Position the entire barcode inside the wide box
                            </li>
                            <li>
                                Hold steady and ensure good lighting (try the
                                light button)
                            </li>
                            <li>Keep the barcode flat and avoid glare</li>
                            <li>
                                Try a distance of 6-8 inches from the camera
                            </li>
                            <li>Align the barcode horizontally in the frame</li>
                        </ul>
                    </div>
                </div>

                {/* Action buttons */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '15px',
                    }}
                >
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </Dialog>
    )
}

export default BarCodeScan
